import styled from 'styled-components';

export const Surface = styled.main`
  --review-muted: #a8b6ca;
  max-width: 1560px;
  margin: 0 auto;
  padding: 12px 24px 40px;
  color: #e5edf7;
  font-size: 14px;
  line-height: 1.55;
  h1, h2, h3, p { margin: 0; }
  h1 { font-size: clamp(22px, 2vw, 28px); font-weight: 650; line-height: 1.3; letter-spacing: -.025em; }
  h2 { font-size: 17px; line-height: 1.4; }
  h3 { font-size: 15px; }
  button, input, select, textarea { font-family: inherit; }
  button, a, summary { &:focus-visible { outline: 2px solid #7dd3fc; outline-offset: 3px; } }
  [hidden] { display: none !important; }
  @media (max-width: 700px) { padding: 12px 14px 28px; }
`;

export const Header = styled.header`
  padding: 8px 0 24px;
  .back { display: inline-flex; align-items: center; gap: 6px; color: #a6d8f5; text-decoration: none; font-size: 13px; margin-bottom: 15px; }
  .heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
  .eyebrow { color: #91a5bd; font-size: 12px; font-weight: 650; letter-spacing: .08em; text-transform: uppercase; margin-bottom: 7px; }
  .meta { display: flex; flex-wrap: wrap; align-items: center; gap: 7px 18px; color: var(--review-muted); font-size: 13px; margin-top: 11px; }
  .meta span { display: inline-flex; align-items: center; gap: 6px; overflow-wrap: anywhere; }
  .actions { display: flex; gap: 12px; align-items: center; flex-shrink: 0; }
  @media (max-width: 900px) { .heading { flex-direction: column; } }
`;

export const SmallButton = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 7px;
  padding: 8px 12px; min-height: 36px; border: 1px solid #33465e;
  border-radius: 7px; background: #142033; color: #dceafa; font-size: 13px; font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { border-color: #6eb7df; background: #1b2e44; }
  &:disabled { opacity: .55; cursor: wait; }
`;

export const Journey = styled.ol`
  display: grid; grid-template-columns: repeat(3, 1fr); list-style: none;
  padding: 0; margin: 0 0 22px; border: 1px solid #29374b; border-radius: 10px; overflow: hidden;
  li { display: flex; gap: 12px; align-items: center; padding: 15px 18px; background: #0c1626; }
  li + li { border-left: 1px solid #29374b; }
  li[data-current='true'] { background: #12273c; box-shadow: inset 0 -2px #67c6f4; }
  .step-icon { display: grid; place-items: center; width: 30px; height: 30px; border: 1px solid #3d526a; border-radius: 50%; color: #b5c9dd; flex-shrink: 0; }
  .done { color: #7cddbe; border-color: #376e60; }
  strong { display: block; font-size: 14px; }
  small { display: block; color: var(--review-muted); font-size: 12px; margin-top: 2px; }
  @media (max-width: 700px) { grid-template-columns: 1fr; li + li { border-left: 0; border-top: 1px solid #29374b; } }
`;

export const Layout = styled.div<{ $complete?: boolean }>`
  display: grid; grid-template-columns: ${p => p.$complete ? 'minmax(0, 1fr)' : '250px minmax(0, 1fr)'}; gap: 20px; align-items: start;
  @media (max-width: 1200px) { grid-template-columns: ${p => p.$complete ? 'minmax(0, 1fr)' : '215px minmax(0, 1fr)'}; gap: 14px; }
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;

export const TaskRail = styled.aside`
  border: 1px solid #29374b; border-radius: 10px; background: #0c1524; overflow: hidden;
  .rail-heading { padding: 17px 15px; border-bottom: 1px solid #29374b; }
  .rail-heading p { color: var(--review-muted); font-size: 12px; margin-top: 5px; }
  .rail-footer { padding: 15px; border-top: 1px solid #29374b; }
  .rail-footer p { margin-top: 9px; color: var(--review-muted); font-size: 12px; }
  .tasks { padding: 7px; max-height: 60vh; overflow-y: auto; }
  .group-heading { padding: 12px 12px 4px; color: #a8b6ca; font-size: 12px; }
  .group-heading span { float: right; }
  label { display: block; margin-top: 12px; font-size: 12px; color: #a8b6ca; }
  select { width: 100%; margin-top: 4px; padding: 8px; background: #142033; color: #e5edf7; border: 1px solid #33465e; border-radius: 6px; }
  .empty { color: var(--review-muted); padding: 15px; }
`;

export const TaskButton = styled.button<{ $active: boolean }>`
  display: block; width: 100%; padding: 13px 12px; margin: 3px 0; text-align: left;
  border: 1px solid ${p => p.$active ? '#5293bd' : 'transparent'};
  border-radius: 7px; background: ${p => p.$active ? '#19334a' : 'transparent'};
  color: #e5edf7; cursor: pointer;
  &:hover { background: ${p => p.$active ? '#19334a' : '#142236'}; }
  .category { color: #a8bacd; font-size: 12px; margin-bottom: 4px; }
  strong { display: block; font-size: 14px; font-weight: 650; }
  .reason { display: flex; align-items: center; gap: 6px; color: #edcc82; margin-top: 5px; font-size: 12px; }
  .owner { display: block; margin-top: 5px; color: #a8bacd; font-size: 12px; }
`;

export const Work = styled.section`
  min-width: 0;
  #review-activation { scroll-margin-top: 125px; }
  .work-heading { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px; margin: 1px 0 14px; }
  .work-heading p { color: var(--review-muted); font-size: 13px; margin-top: 3px; }
  .work-pair { display: grid; grid-template-columns: minmax(0, var(--review-pane, 52%)) minmax(0, 1fr); gap: 16px; align-items: start; }
  .work-pair > * { min-width: 0; }
  .pane-size { display: flex; align-items: center; justify-content: flex-end; gap: 10px; margin-bottom: 12px; color: #a8b6ca; font-size: 12px; }
  .pane-size input { width: 110px; accent-color: #67c6f4; }
  .work-pair > div:last-child { position: sticky; top: 125px; }
  .source-heading { display: flex; align-items: center; gap: 7px; color: #a8cfe8; font-size: 12px; font-weight: 600; margin-bottom: 9px; }
  @media (max-width: 1150px) { .work-pair { grid-template-columns: 1fr; } .pane-size { display: none; } .work-pair > div:last-child { position: static; } }
`;

/** Typography is scoped to this opt-in presentation, including the shared editors. */
export const Readable = styled.div`
  font-size: 14px;
  /* Cards bring their own surface and state colour; only spacing is normalised here. */
  & section { border-radius: 9px; }
  & article { padding: 18px; border-radius: 9px; }
  & article > button { align-items: start; cursor: default; }
  & div { font-size: 13px; }
  & h2 { font-size: 17px; }
  & p, & li, & blockquote { font-size: 14px; line-height: 1.6; }
  & label { font-size: 13px; color: #b6c7da; }
  & input, & select, & textarea { font-size: 14px; min-height: 40px; }
  & input[type='checkbox'] { min-height: auto; }
  & button { font-size: 13px; min-height: 36px; }
  & button span { font-size: 13px; }
  & article > button > span { align-self: start; min-height: auto; padding: 4px 7px; font-size: 12px; }
  & [role='dialog'] { font-size: 14px; }
  & form { gap: 13px; margin-top: 16px; padding-top: 16px; }
  & input::placeholder, & textarea::placeholder { color: #91a4bb; }
  & .source { color: #a8b6ca; }
  & iframe { min-height: 460px; }
`;

export const Note = styled.div`
  padding: 14px 16px; border: 1px solid #36485e; border-radius: 8px; background: #101c2b;
  font-size: 14px; color: #c5d4e6;
  strong { color: #edf4ff; }
  p + p { margin-top: 8px; }
`;

export const Signoff = styled.details`
  margin-top: 22px; border: 1px solid #29374b; border-radius: 10px; background: #0c1524;
  > summary { cursor: pointer; padding: 16px 18px; font-size: 14px; font-weight: 650; }
  > summary small { color: var(--review-muted); font-size: 13px; font-weight: 400; margin-left: 12px; }
  > div { padding: 0 16px 16px; }
  &[open] > summary { border-bottom: 1px solid #29374b; margin-bottom: 16px; }
`;

export const Record = styled.section`
  margin-top: 24px; scroll-margin-top: 125px;
`;

export const SummaryGrid = styled.div`
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 20px;
  margin: 0 0 24px; padding: 16px 0; border-bottom: 1px solid #29374b;
  span { display: block; color: #a8b6ca; font-size: 12px; margin-bottom: 5px; }
  strong { font-size: 15px; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
  @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
`;

export const DecisionBar = styled.footer`
  position: sticky; bottom: 12px; z-index: 20;
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  margin-top: 24px; padding: 14px 18px; background: #142438;
  border: 1px solid #43617f; border-radius: 10px; box-shadow: 0 8px 32px #0006;
  strong, span { display: block; }
  span { color: #b6c7da; font-size: 13px; margin-top: 3px; }
  button { flex-shrink: 0; border-color: #67c6f4; }
  @media (max-width: 700px) { flex-direction: column; align-items: stretch; }
`;
